import {
  OnInit} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostBinding,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import {
  NavigationDropdown,
  NavigationItem,
  NavigationLink
} from '../../../../core/navigation/navigation-item.interface';
import { dropdownAnimation } from '@vex/animations/dropdown.animation';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive
} from '@angular/router';
import { combineLatest, merge } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { NavigationService } from '../../../../core/navigation/navigation.service';

import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { VexLayoutService } from '@vex/services/vex-layout.service';

@Component({
    selector: 'vex-sidenav-item',
    templateUrl: './sidenav-item.component.html',
    styleUrls: ['./sidenav-item.component.scss'],
    animations: [dropdownAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    MatRippleModule,
    RouterLinkActive,
    RouterLink,
    MatIconModule,
    MatTooltipModule,
    NgClass
]
})
export class SidenavItemComponent implements OnInit {
  private router = inject(Router);
  private cd = inject(ChangeDetectorRef);
  private navigationService = inject(NavigationService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly layoutService = inject(VexLayoutService);

  /**
   * True when the sidenav shows icons only (collapsed AND not hover-expanded):
   * the label is hidden, so a top-level item surfaces its name as a tooltip
   * (redesign §2 — "tooltips on hover" on the icon rail). Single source = the
   * layout service, so it stays in sync with the rail the LayoutComponent drives.
   */
  readonly labelsHidden = toSignal(
    combineLatest([
      this.layoutService.sidenavCollapsed$,
      this.layoutService.sidenavCollapsedOpen$
    ]).pipe(map(([collapsed, hoverOpen]) => collapsed && !hoverOpen)),
    { initialValue: false }
  );

  readonly item = input.required<NavigationItem>();
  readonly level = input.required<number>();

  /**
   * Bumped on EVERY translation-mutating ngx-translate event. Read by the
   * `translatedLabel` computed so it re-evaluates on each language switch.
   */
  private readonly _translationVersion = signal(0);

  /**
   * Reactive translation of the item label for the persistent, OnPush sidenav.
   *
   * History: `{{ label | translate }}` then `translate.stream()` both stopped
   * updating after a couple of switches on this long-lived OnPush view.
   *  - The pipe relies on impure-pipe markForCheck, fragile on persistent views.
   *  - `stream()` only re-emits on `onLangChange`; this app also mutates
   *    translations via `onTranslationChange` (TranslationService.setTranslation
   *    on Firestore revalidate/publish) and `use()` only calls the loader when a
   *    lang isn't already cached — so a single channel is unreliable per switch.
   *
   * Robust fix: a `computed` that depends on BOTH the item signal AND
   * `_translationVersion` (bumped by an explicit subscription to all three
   * translation events). It re-reads `instant(key)` and, being a signal, marks
   * this OnPush view dirty natively — no toObservable/switchMap/stream subtlety.
   */
  readonly translatedLabel = computed(() => {
    this._translationVersion(); // re-evaluate on any translation event
    const key = this.item()?.label?.trim();
    return key ? this.translateService.instant(key) : '';
  });

  /**
   * Desktop-only keyboard shortcut label (hidden below md via CSS).
   * Resolves an i18n key when `keyboardShortcut` looks like one.
   */
  readonly keyboardShortcutLabel = computed(() => {
    this._translationVersion();
    if (this.layoutService.isLtMd()) return '';
    const item = this.item();
    if (!item || item.type !== 'link' || !item.keyboardShortcut) return '';
    const key = item.keyboardShortcut.trim();
    if (!key) return '';
    const translated = this.translateService.instant(key);
    return typeof translated === 'string' && translated !== key ? translated : key;
  });

  isOpen = false;
  isActive = false;

  isLink = this.navigationService.isLink;
  isDropdown = this.navigationService.isDropdown;
  isSubheading = this.navigationService.isSubheading;

  constructor() {
    // Re-translate the label on EVERY translation-mutating event so this
    // persistent OnPush sidenav reliably follows each language switch.
    merge(
      this.translateService.onLangChange,
      this.translateService.onTranslationChange,
      this.translateService.onFallbackLangChange
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._translationVersion.update((v) => v + 1);
        this.cd.markForCheck();
      });

    // React to item changes — replaces the old ngOnChanges hook.
    effect(() => {
      const item = this.item();
      if (this.isDropdown(item)) {
        this.onRouteChange();
      }
    });
  }

  @HostBinding('class')
  get levelClass() {
    return `item-level-${this.level()}`;
  }

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        filter(() => this.isDropdown(this.item())),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.onRouteChange());

    this.navigationService.openChange$
      .pipe(
        filter(() => this.isDropdown(this.item())),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((item) => this.onOpenChange(item));
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
    this.navigationService.triggerOpenChange(this.item() as NavigationDropdown);
    this.cd.markForCheck();
  }

  onOpenChange(item: NavigationDropdown) {
    if (this.isChildrenOf(this.item() as NavigationDropdown, item)) {
      return;
    }

    if (this.hasActiveChilds(this.item() as NavigationDropdown)) {
      return;
    }

    if (this.item() !== item) {
      this.isOpen = false;
      this.cd.markForCheck();
    }
  }

  onRouteChange() {
    if (this.hasActiveChilds(this.item() as NavigationDropdown)) {
      this.isActive = true;
      this.isOpen = true;
      this.navigationService.triggerOpenChange(this.item() as NavigationDropdown);
      this.cd.markForCheck();
    } else {
      this.isActive = false;
      this.isOpen = false;
      this.navigationService.triggerOpenChange(this.item() as NavigationDropdown);
      this.cd.markForCheck();
    }
  }

  isChildrenOf(parent: NavigationDropdown, item: NavigationDropdown): boolean {
    if (parent.children.indexOf(item) !== -1) {
      return true;
    }

    return parent.children
      .filter((child) => this.isDropdown(child))
      .some((child) => this.isChildrenOf(child as NavigationDropdown, item));
  }

  hasActiveChilds(parent: NavigationDropdown): boolean {
    return parent.children.some((child) => {
      if (this.isDropdown(child)) {
        return this.hasActiveChilds(child);
      }

      if (this.isLink(child) && !this.isFunction(child.route)) {
        return this.router.isActive(child.route as string, false);
      }

      return false;
    });
  }

  isFunction(prop: NavigationLink['route']): boolean {
    return prop instanceof Function;
  }
}
